/* Super-admin home + security page (iter98.99fm).
   ────────────────────────────────────────────────────────────────
   - /super-admin           → home (roster + who-am-I)
   - /super-admin/security  → manage passkeys + invite peers + peer-reset
*/
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { startRegistration } from "@simplewebauthn/browser";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
    ShieldCheck, Fingerprint, Copy, Plus, TrashSimple, ArrowLeft,
    Warning, LifebuoyIcon, ArrowsClockwise, Sparkle, UserPlus,
} from "@phosphor-icons/react";

export default function SuperAdminHome() {
    const [me, setMe] = useState(null);
    const [roster, setRoster] = useState([]);
    const [loading, setLoading] = useState(true);

    const reload = async () => {
        try {
            const [m, r] = await Promise.all([
                api.get("/super-admin/me"),
                api.get("/super-admin/roster"),
            ]);
            setMe(m.data);
            setRoster(r.data);
        } catch (err) {
            if (err.response?.status === 403 || err.response?.status === 401) {
                window.location.href = "/super-admin-login";
            } else {
                toast.error(err.response?.data?.detail || "Failed to load");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { reload(); }, []);

    if (loading) return <SAShell><div className="text-slate-400 text-sm">Loading…</div></SAShell>;
    if (!me) return null;

    return (
        <SAShell>
            <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-lg bg-primary/20 border border-primary/30 grid place-items-center text-primary">
                    <ShieldCheck size={22} weight="fill" />
                </div>
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary/80">Signed in as</div>
                    <div className="font-semibold">{me.name} <span className="text-slate-500 text-xs">· {me.email || me.id}</span></div>
                </div>
                <div className="ml-auto flex gap-2">
                    <Link to="/super-admin/security" className="text-xs text-primary hover:underline" data-testid="sa-home-security-link">
                        Security settings →
                    </Link>
                </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-4 mb-8">
                <StatCard label="Registered passkeys" value={me.passkey_count} icon={<Fingerprint size={16} weight="duotone" />} />
                <StatCard label="Recovery codes remaining" value={me.recovery_codes_remaining} icon={<LifebuoyIcon size={16} weight="duotone" />} tone={me.recovery_codes_remaining <= 3 ? "warn" : "ok"} />
                <StatCard label="Total super admins" value={roster.length} icon={<ShieldCheck size={16} weight="duotone" />} />
            </div>

            <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                    <UserPlus size={16} weight="duotone" className="text-primary" />
                    <h2 className="font-display text-lg font-semibold">Super-admin roster</h2>
                </div>
                <div className="divide-y divide-slate-800" data-testid="sa-home-roster">
                    {roster.map((r) => (
                        <div key={r.user_id} className="py-3 flex items-center gap-3 flex-wrap">
                            <div className="w-8 h-8 rounded-full bg-slate-800 grid place-items-center text-xs font-bold text-slate-300">
                                {(r.name || "?").slice(0, 1).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                                <div className="text-sm font-semibold text-slate-100 truncate flex items-center gap-2">
                                    {r.name}
                                    {r.is_primary && <Pill tone="primary" label="Primary" />}
                                </div>
                                <div className="text-[11px] text-slate-500 truncate">{r.email || r.phone || r.user_id}</div>
                            </div>
                            <div className="ml-auto flex items-center gap-2 flex-wrap">
                                <Pill tone={r.passkey_count > 0 ? "ok" : "warn"} label={`${r.passkey_count} passkey${r.passkey_count === 1 ? "" : "s"}`} />
                                {r.needs_reenrolment && <Pill tone="warn" label="needs re-enrol" />}
                                <span className="text-[10px] text-slate-500">enrolled {relAge(r.enrolled_at)}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </SAShell>
    );
}

/* ═══════════════════════════════════════════════════════════════════════
   Security page — manage passkeys + invite peers + peer-reset
   ═══════════════════════════════════════════════════════════════════════ */

export function SuperAdminSecurity() {
    const location = useLocation();
    const navigate = useNavigate();
    const [passkeys, setPasskeys] = useState([]);
    const [roster, setRoster] = useState([]);
    const [me, setMe] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [showInvite, setShowInvite] = useState(false);

    const searchParams = new URLSearchParams(location.search);
    const forceAddPasskey = searchParams.get("add_passkey") === "1";

    const reload = async () => {
        try {
            const [pk, rr, m] = await Promise.all([
                api.get("/super-admin/passkeys"),
                api.get("/super-admin/roster"),
                api.get("/super-admin/me"),
            ]);
            setPasskeys(pk.data);
            setRoster(rr.data);
            setMe(m.data);
        } catch (err) {
            if (err.response?.status === 403 || err.response?.status === 401) {
                window.location.href = "/super-admin-login";
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { reload(); }, []);

    const addPasskey = async (label) => {
        setBusy(true);
        try {
            const opts = await api.post("/super-admin/passkeys/registration-options");
            const attResp = await startRegistration({ optionsJSON: opts.data.options });
            await api.post("/super-admin/passkeys/verify-registration", {
                credential: attResp,
                label: label || `Passkey · ${navigator.platform}`,
            });
            toast.success("Passkey added");
            await reload();
            if (forceAddPasskey) navigate("/super-admin/security", { replace: true });
        } catch (err) {
            const msg = err?.name === "NotAllowedError"
                ? "Passkey prompt was cancelled"
                : (err.response?.data?.detail || err.message || "Failed to add passkey");
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    const revokePasskey = async (pkId) => {
        if (!window.confirm("Revoke this passkey? Make sure you still have at least one working device.")) return;
        try {
            await api.delete(`/super-admin/passkeys/${pkId}`);
            toast.success("Passkey revoked");
            await reload();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to revoke");
        }
    };

    if (loading) return <SAShell><div className="text-slate-400 text-sm">Loading…</div></SAShell>;

    return (
        <SAShell>
            <div className="flex items-center gap-2 mb-6">
                <Link to="/super-admin" className="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1" data-testid="sa-security-back">
                    <ArrowLeft size={12} /> Super Admin
                </Link>
            </div>

            {forceAddPasskey && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6 flex items-start gap-3" data-testid="sa-security-recovery-warning">
                    <Warning size={18} weight="duotone" className="text-amber-400 shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-100">
                        You logged in with a recovery code. <span className="font-semibold">Add a new passkey now</span> so you can log in normally next time.
                    </div>
                </div>
            )}

            <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6 mb-6" data-testid="sa-security-passkeys">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Fingerprint size={18} weight="duotone" className="text-primary" />
                    <h2 className="font-display text-lg font-semibold">Your passkeys</h2>
                    <span className="text-[10px] font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-slate-800 text-slate-300 border border-slate-700">
                        {passkeys.length}
                    </span>
                </div>
                <p className="text-xs text-slate-400 mb-4">
                    Unlimited passkeys per account. Register your laptop, phone and a hardware key for redundancy.
                </p>

                <div className="divide-y divide-slate-800 mb-4">
                    {passkeys.map((p) => (
                        <div key={p.id} className="py-3 flex items-center gap-3 flex-wrap" data-testid={`sa-passkey-${p.id}`}>
                            <div className="w-8 h-8 rounded-lg bg-slate-800 border border-slate-700 grid place-items-center text-slate-300">
                                <Fingerprint size={14} weight="duotone" />
                            </div>
                            <div className="min-w-0">
                                <div className="text-sm font-semibold text-slate-100 truncate">{p.label}</div>
                                <div className="text-[11px] text-slate-500">
                                    Added {relAge(p.created_at)}
                                    {p.last_used_at ? ` · last used ${relAge(p.last_used_at)}` : " · never used"}
                                </div>
                            </div>
                            <Button
                                variant="outline" size="sm"
                                className="ml-auto border-rose-500/30 text-rose-300 hover:bg-rose-500/10 hover:text-rose-200"
                                onClick={() => revokePasskey(p.id)}
                                data-testid={`sa-passkey-revoke-${p.id}`}>
                                <TrashSimple size={12} weight="bold" className="mr-1" />
                                Revoke
                            </Button>
                        </div>
                    ))}
                </div>

                <AddPasskeyRow onAdd={addPasskey} busy={busy} forceExpand={forceAddPasskey} />
            </div>

            <InvitePanel show={showInvite} onToggle={() => setShowInvite((s) => !s)} onIssued={reload} />

            <PeerResetPanel roster={roster} me={me} onReset={reload} />
        </SAShell>
    );
}

function AddPasskeyRow({ onAdd, busy, forceExpand }) {
    const [label, setLabel] = useState("");
    const [expanded, setExpanded] = useState(!!forceExpand);
    if (!expanded) {
        return (
            <Button className="w-full bg-primary hover:bg-primary/90"
                onClick={() => setExpanded(true)}
                data-testid="sa-security-add-passkey-btn">
                <Plus size={14} weight="bold" className="mr-1" />
                Add another passkey
            </Button>
        );
    }
    return (
        <div className="rounded-xl border border-slate-700 bg-slate-950/50 p-4">
            <Label className="text-xs font-semibold text-slate-300">Device label (optional)</Label>
            <Input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder={`e.g. ${navigator.platform} · YubiKey · Phone`}
                className="mt-1 mb-3 bg-slate-950 border-slate-700 text-slate-100"
                data-testid="sa-security-add-passkey-label"
            />
            <Button className="w-full bg-primary hover:bg-primary/90"
                onClick={() => onAdd(label)}
                disabled={busy}
                data-testid="sa-security-add-passkey-register-btn">
                {busy ? "Waiting for device…" : "Register passkey"}
                <Fingerprint size={14} weight="bold" className="ml-1.5" />
            </Button>
        </div>
    );
}

function InvitePanel({ show, onToggle, onIssued }) {
    const [form, setForm] = useState({ email: "", phone: "", name: "" });
    const [issued, setIssued] = useState(null);
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!form.email || !form.phone || !form.name) {
            toast.error("Fill all three fields");
            return;
        }
        setBusy(true);
        try {
            const r = await api.post("/super-admin/invites", form);
            setIssued(r.data);
            setForm({ email: "", phone: "", name: "" });
            toast.success("Invite issued + emailed");
            onIssued?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to issue invite");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6 mb-6" data-testid="sa-security-invite">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
                <UserPlus size={18} weight="duotone" className="text-primary" />
                <h2 className="font-display text-lg font-semibold">Invite a super admin</h2>
                <Button variant="ghost" size="sm"
                    className="ml-auto text-slate-300 hover:bg-slate-800"
                    onClick={onToggle}
                    data-testid="sa-invite-toggle">
                    {show ? "Cancel" : "New invite"}
                </Button>
            </div>
            {show && (
                <div className="pt-3 space-y-3">
                    <div className="grid sm:grid-cols-3 gap-3">
                        <Field label="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="sa-invite-name" />
                        <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="sa-invite-email" />
                        <Field label="Phone (E.164)" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="sa-invite-phone" />
                    </div>
                    <Button className="bg-primary hover:bg-primary/90"
                        onClick={submit} disabled={busy}
                        data-testid="sa-invite-issue-btn">
                        {busy ? "Issuing…" : "Issue invite + email"}
                    </Button>

                    {issued && (
                        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4" data-testid="sa-invite-issued">
                            <div className="text-sm text-emerald-100 font-semibold mb-1">Invite issued</div>
                            <div className="text-xs text-emerald-200/80 mb-2">Expires {new Date(issued.expires_at).toLocaleString()}</div>
                            <div className="flex items-center gap-2 bg-slate-950 border border-slate-800 rounded-lg px-3 py-2">
                                <code className="text-xs text-slate-300 flex-1 truncate">{issued.link}</code>
                                <Button variant="ghost" size="sm" className="text-slate-300 hover:bg-slate-800"
                                    onClick={() => { navigator.clipboard.writeText(issued.link); toast.success("Copied"); }}
                                    data-testid="sa-invite-copy-link">
                                    <Copy size={12} weight="bold" />
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function PeerResetPanel({ roster, me, onReset }) {
    const [busy, setBusy] = useState(false);
    const others = (roster || []).filter((r) => r.user_id !== me?.id);

    const reset = async (target) => {
        const reason = window.prompt(`Reset all passkeys + recovery codes for ${target.name}?\nType a reason (audit log).`);
        if (!reason) return;
        setBusy(true);
        try {
            await api.post("/super-admin/peer-reset", { target_user_id: target.user_id, reason });
            toast.success(`${target.name} reset — they need a fresh invite`);
            onReset?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Reset failed");
        } finally {
            setBusy(false);
        }
    };

    if (others.length === 0) return null;
    return (
        <div className="bg-slate-900/60 border border-rose-500/30 rounded-2xl p-6" data-testid="sa-security-peer-reset">
            <div className="flex items-center gap-2 mb-1">
                <ArrowsClockwise size={18} weight="duotone" className="text-rose-400" />
                <h2 className="font-display text-lg font-semibold text-rose-100">Peer reset</h2>
            </div>
            <p className="text-xs text-slate-400 mb-4">
                Use this if a peer super admin loses all their devices AND their recovery codes. Revokes every passkey + recovery code on their account, forcing them to re-enrol via a fresh invite.
            </p>
            <div className="divide-y divide-slate-800">
                {others.map((r) => (
                    <div key={r.user_id} className="py-3 flex items-center gap-3 flex-wrap">
                        <div className="min-w-0">
                            <div className="text-sm font-semibold text-slate-100">{r.name}</div>
                            <div className="text-[11px] text-slate-500">{r.email || r.phone}</div>
                        </div>
                        <Button
                            variant="outline" size="sm"
                            className="ml-auto border-rose-500/30 text-rose-300 hover:bg-rose-500/10"
                            onClick={() => reset(r)} disabled={busy}
                            data-testid={`sa-peer-reset-${r.user_id}`}>
                            Reset passkeys
                        </Button>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ── small atoms ─────────────────────────────────────────────────────── */

function SAShell({ children }) {
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100 px-4 py-10">
            <div className="max-w-3xl mx-auto">
                {children}
            </div>
        </div>
    );
}

function StatCard({ label, value, icon, tone }) {
    const toneClass = tone === "warn"
        ? "border-amber-500/30 bg-amber-500/10 text-amber-100"
        : "border-slate-800 bg-slate-900/60 text-slate-100";
    return (
        <div className={`rounded-2xl border p-4 ${toneClass}`}>
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider opacity-80">
                {icon} {label}
            </div>
            <div className="text-2xl font-display font-semibold mt-1">{value}</div>
        </div>
    );
}

function Pill({ tone, label }) {
    const cls = tone === "ok"
        ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
        : tone === "warn"
            ? "bg-amber-500/10 text-amber-300 border-amber-500/30"
            : tone === "primary"
                ? "bg-primary/20 text-primary border-primary/40"
                : "bg-slate-800 text-slate-300 border-slate-700";
    return (
        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded-full border ${cls}`}>
            {label}
        </span>
    );
}

function Field({ label, value, onChange, testid, type = "text" }) {
    return (
        <div>
            <Label className="text-xs font-semibold text-slate-300">{label}</Label>
            <Input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="mt-1 bg-slate-950 border-slate-700 text-slate-100"
                data-testid={testid}
            />
        </div>
    );
}

function relAge(iso) {
    if (!iso) return "—";
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 60_000) return "just now";
    if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
    if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
    return `${Math.floor(ms / 86_400_000)}d ago`;
}
